import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import { _getContextThreads } from '@/api/chat/queries';
import { CHAT_QUERY_KEYS, type ChatIdentityKey, chatQueryKey } from '@/api/chat/query-keys';
import { setQueryDataKeepingError } from '@/api/chat/set-query-data';
import type {
  ContextThreadRow,
  GetContextMessagesRes,
  GetContextThreadsRes,
} from '@/api/chat/v2-types';

import type { ThreadSummary } from '../types';

const threadRowFromSummary = (thread: ThreadSummary): ContextThreadRow => ({
  id: thread.id,
  name: thread.name,
  preview: thread.preview,
  lastActivityAt: thread.lastActivityAt,
  unreadCount: thread.unreadCount,
  isBroadcast: thread.isBroadcast,
  isOwnBroadcast: Boolean(thread.isOwnBroadcast),
  isArchived: Boolean(thread.isArchived),
  isResolved: Boolean(thread.isResolved),
  isMuted: thread.isMuted,
  isPrivate: thread.isPrivate,
  isWaiting: thread.isWaiting,
  ownIdentity: thread.ownIdentity,
  awaitingMyReply: Boolean(thread.awaitingMyReply),
  attachmentCount: thread.attachmentCount ?? 0,
  topics: thread.topics.map((topic) => ({
    ...topic,
    isSystem: false,
    locked: topic.locked === true,
  })),
  filedContextIds: thread.filedContextIds ?? [],
  parentThreadId: thread.parentThreadId ?? null,
  subject: thread.subject ?? null,
});

export const seedContextThread = (
  client: QueryClient,
  identity: ChatIdentityKey,
  contextId: string,
  thread: ThreadSummary,
): void => {
  client.setQueryData<GetContextThreadsRes>(
    _getContextThreads(identity, { contextId, includeResolved: true }).queryKey,
    (data) => {
      if (!data || data.rows.some((row) => row.id === thread.id)) return data;
      return { ...data, rows: [threadRowFromSummary(thread), ...data.rows] };
    },
  );
};

type ContextMessagesCache = InfiniteData<GetContextMessagesRes>;

export const feedIsScopedToThread = (queryKey: readonly unknown[], threadId: string): boolean =>
  (queryKey[3] as { threadId?: string } | undefined)?.threadId === threadId;

export const dropThreadFromCaches = (
  client: QueryClient,
  identity: ChatIdentityKey,
  threadId: string,
): void => {
  client.setQueriesData<GetContextThreadsRes>(
    { queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contextThreads) },
    (data) => (data ? { ...data, rows: data.rows.filter((row) => row.id !== threadId) } : data),
  );
  client.setQueriesData<InfiniteData<GetContextThreadsRes>>(
    { queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contextThreadsPaged) },
    (data) =>
      data
        ? {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              rows: page.rows.filter((row) => row.id !== threadId),
            })),
          }
        : data,
  );
  for (const [queryKey] of client.getQueriesData<ContextMessagesCache>({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contextMessages),
  })) {
    setQueryDataKeepingError<ContextMessagesCache>(client, queryKey, (data) =>
      data
        ? {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              entries: page.entries.filter((entry) => entry.threadId !== threadId),
            })),
          }
        : data,
    );
  }
  client.removeQueries({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.participants, threadId),
  });
};
